import React, { useState, useEffect } from 'react';
import { getDomains, Domain } from '../api/data';

const EMBED_BASE_URL = 'https://chatbot.najahapps.com';

const CopyButton: React.FC<{ text: string }> = ({ text }) => {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    });
  };
  return (
    <button
      onClick={copy}
      className="absolute top-2.5 right-2.5 inline-flex items-center gap-1.5 px-2.5 py-1 bg-white border border-primary-200 text-primary-600 text-xs font-medium rounded-md hover:border-primary-300 hover:text-primary-900 transition-colors"
    >
      {copied ? (
        <>
          <svg className="w-3.5 h-3.5 text-emerald-600" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
          </svg>
          Copied
        </>
      ) : (
        <>
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" strokeWidth={1.7} stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 17.25v3.375c0 .621-.504 1.125-1.125 1.125h-9.75a1.125 1.125 0 01-1.125-1.125V7.875c0-.621.504-1.125 1.125-1.125H6.75a9.06 9.06 0 011.5.124m7.5 10.376h3.375c.621 0 1.125-.504 1.125-1.125V11.25c0-4.46-3.243-8.161-7.5-8.876a9.06 9.06 0 00-1.5-.124H9.375c-.621 0-1.125.504-1.125 1.125v3.5m7.5 10.375H9.375a1.125 1.125 0 01-1.125-1.125v-9.25m12 6.625v-1.875a3.375 3.375 0 00-3.375-3.375h-1.5a1.125 1.125 0 01-1.125-1.125v-1.5a3.375 3.375 0 00-3.375-3.375H9.75" />
          </svg>
          Copy
        </>
      )}
    </button>
  );
};

const CodeBlock: React.FC<{ code: string }> = ({ code }) => (
  <div className="relative">
    <pre className="bg-primary-50 border border-primary-200 text-primary-800 rounded-lg p-4 pr-24 text-sm leading-relaxed font-mono overflow-x-auto whitespace-pre">{code}</pre>
    <CopyButton text={code} />
  </div>
);

const ChatWidget: React.FC = () => {
  const [lang, setLang] = useState<'ar' | 'en'>('en');
  const [previewDomain, setPreviewDomain] = useState('');
  const [embedLang, setEmbedLang] = useState<'ar' | 'en'>('en');
  const [embedDomain, setEmbedDomain] = useState('');
  const [activeTab, setActiveTab] = useState<'preview' | 'embed'>('preview');
  const [domains, setDomains] = useState<Domain[]>([]);

  useEffect(() => {
    getDomains(1, 100).then(res => setDomains(res.items)).catch(() => {});
  }, []);

  const activeDomains = domains.filter(d => d.is_active);
  const widgetPreviewUrl = `/widget?lang=${lang}${previewDomain ? `&domain=${previewDomain}` : ''}`;

  const embedSnippet = embedDomain
    ? `<!-- NNU Chatbot Widget -->
<script>
  window.NNU_CHAT_CONFIG = { lang: '${embedLang}', domain: '${embedDomain}' };
</script>
<script src="${EMBED_BASE_URL}/widget/embed.js?v=1" defer></script>`
    : `<!-- NNU Chatbot Widget -->
<script>
  window.NNU_CHAT_CONFIG = { lang: '${embedLang}' };
</script>
<script src="${EMBED_BASE_URL}/widget/embed.js?v=1" defer></script>`;

  const embedMinimal = `<script src="${EMBED_BASE_URL}/widget/embed.js?v=1" defer></script>`;

  const fullExample = `<!DOCTYPE html>
<html>
<head>
  <title>My University Website</title>
</head>
<body>
  <h1>Welcome to our website</h1>

  <!-- NNU Chatbot Widget -->
  <script>
    window.NNU_CHAT_CONFIG = {
      lang: 'en',
      position: 'right'${embedDomain ? `,\n      domain: '${embedDomain}'` : ''}
    };
  </script>
  <script src="${EMBED_BASE_URL}/widget/embed.js?v=1" defer></script>
</body>
</html>`;

  return (
    <div className="max-w-6xl mx-auto px-6 lg:px-8 py-8 h-[calc(100vh-3.5rem)] flex flex-col">
      {/* Header */}
      <div className="flex-shrink-0">
        <h1 className="text-3xl font-semibold tracking-tight text-primary-900 leading-tight">Chat Widget</h1>
        <p className="text-sm text-primary-500 mt-1.5">Try the widget as visitors see it, or grab the snippet to embed it on any site.</p>

        {/* Tabs */}
        <div className="mt-6 border-b border-primary-200 flex items-center gap-6">
          {([['preview', 'Preview'], ['embed', 'Embed on a website']] as const).map(([key, label]) => (
            <button
              key={key}
              onClick={() => setActiveTab(key)}
              className={`pb-2.5 -mb-px text-sm font-medium border-b-2 transition-colors ${
                activeTab === key
                  ? 'text-primary-900 border-primary'
                  : 'text-primary-500 border-transparent hover:text-primary-800'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {activeTab === 'preview' && (
        <div className="flex-1 flex flex-col min-h-0 pt-5">
          <div className="mb-4 flex items-center justify-between flex-wrap gap-3 flex-shrink-0">
            <span className="text-sm text-primary-500">Live preview — messages go to the real chatbot.</span>
            <div className="flex items-center gap-2">
              <select
                value={previewDomain}
                onChange={(e) => setPreviewDomain(e.target.value)}
                className="input-base text-sm py-1.5 w-auto min-w-[150px]"
              >
                <option value="">General</option>
                {activeDomains.map(d => <option key={d.slug} value={d.slug}>{d.name_en}</option>)}
              </select>
              <div className="inline-flex items-center bg-primary-100 rounded-lg p-0.5">
                {(['en', 'ar'] as const).map(l => (
                  <button
                    key={l}
                    onClick={() => setLang(l)}
                    className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                      lang === l ? 'bg-white text-primary-900 shadow-card' : 'text-primary-500 hover:text-primary-800'
                    }`}
                  >
                    {l === 'en' ? 'English' : 'العربية'}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <div className="flex-1 w-full bg-white rounded-xl border border-primary-200 overflow-hidden">
            <iframe
              key={`${lang}-${previewDomain}`}
              src={widgetPreviewUrl}
              className="w-full h-full border-0"
              title="NNU Chatbot Widget"
            />
          </div>
        </div>
      )}

      {activeTab === 'embed' && (
        <div className="flex-1 overflow-y-auto pt-5 pb-2 space-y-5">
          {/* Quick Start */}
          <div className="card p-6">
            <h3 className="text-sm font-semibold text-primary-900">Quick start</h3>
            <p className="text-sm text-primary-500 mt-1 mb-4">
              One line before the closing <code className="text-xs bg-primary-100 px-1.5 py-0.5 rounded">&lt;/body&gt;</code> tag.
              Defaults to Arabic, all topics. Works on WordPress, Wix, Squarespace, or plain HTML.
            </p>
            <CodeBlock code={embedMinimal} />
          </div>

          {/* Customized */}
          <div className="card p-6">
            <h3 className="text-sm font-semibold text-primary-900">Customized embed</h3>
            <p className="text-sm text-primary-500 mt-1 mb-4">Pick a language and an optional domain, then copy the snippet.</p>

            <div className="flex items-center gap-5 mb-4 flex-wrap">
              <div className="flex items-center gap-2.5">
                <span className="text-sm text-primary-500">Language</span>
                <div className="inline-flex items-center bg-primary-100 rounded-lg p-0.5">
                  {(['en', 'ar'] as const).map(l => (
                    <button
                      key={l}
                      onClick={() => setEmbedLang(l)}
                      className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${
                        embedLang === l ? 'bg-white text-primary-900 shadow-card' : 'text-primary-500 hover:text-primary-800'
                      }`}
                    >
                      {l === 'en' ? 'English' : 'العربية'}
                    </button>
                  ))}
                </div>
              </div>
              {activeDomains.length > 0 && (
                <div className="flex items-center gap-2.5">
                  <span className="text-sm text-primary-500">Domain</span>
                  <select value={embedDomain} onChange={(e) => setEmbedDomain(e.target.value)} className="input-base text-sm py-1.5 w-auto min-w-[160px]">
                    <option value="">General (all topics)</option>
                    {activeDomains.map(d => <option key={d.slug} value={d.slug}>{d.name_en}</option>)}
                  </select>
                </div>
              )}
            </div>

            <CodeBlock code={embedSnippet} />

            {embedDomain && (
              <div className="mt-4 px-4 py-3 bg-primary-50 rounded-lg border border-primary-200">
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-xs font-medium text-primary-700">Direct link (QR-friendly)</div>
                    <code className="block mt-1 text-xs text-primary-500 break-all">
                      {EMBED_BASE_URL}/widget?domain={embedDomain}&lang={embedLang}
                    </code>
                  </div>
                  <div className="relative w-20 h-7 flex-shrink-0">
                    <CopyButton text={`${EMBED_BASE_URL}/widget?domain=${embedDomain}&lang=${embedLang}`} />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Config Options */}
          <div className="card p-6">
            <h3 className="text-sm font-semibold text-primary-900 mb-4">Configuration options</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-primary-200">
                    <th className="text-left py-2 pr-4 eyebrow font-semibold">Option</th>
                    <th className="text-left py-2 pr-4 eyebrow font-semibold">Default</th>
                    <th className="text-left py-2 eyebrow font-semibold">Description</th>
                  </tr>
                </thead>
                <tbody className="text-primary-700">
                  {[
                    ['lang', '"ar"', 'Widget language: "ar" or "en"'],
                    ['domain', 'null', 'Domain slug for a scoped chatbot (e.g. "hr")'],
                    ['position', '"right"', 'Bubble position: "right" or "left"'],
                    ['baseUrl', 'auto', 'Override the chatbot server URL (advanced)'],
                    ['shortcut', 'false', 'Ctrl/⌘+K opens the chat (off by default so it never fights the browser or the host site)'],
                    ['style', '"aurora"', 'Launcher look: "aurora" (turning ring + soft glow) or "plain" (flat disc)'],
                    ['greeting', 'true', 'Show the persistent message pill next to the bubble (hidden on phones and while the chat is open)'],
                    ['greetingText', 'per language', 'Custom pill text, e.g. "اسأل كلية الطب"'],
                  ].map(([opt, def, desc]) => (
                    <tr key={opt} className="border-b border-primary-100 last:border-0">
                      <td className="py-2.5 pr-4 font-mono text-xs text-primary-700">{opt}</td>
                      <td className="py-2.5 pr-4 font-mono text-xs text-primary-500">{def}</td>
                      <td className="py-2.5 text-sm">{desc}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Full Example */}
          <div className="card p-6">
            <h3 className="text-sm font-semibold text-primary-900">Full example</h3>
            <p className="text-sm text-primary-500 mt-1 mb-4">A complete HTML page with the widget embedded.</p>
            <CodeBlock code={fullExample} />
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatWidget;
